/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export const LuisConstants = {
    Azure: {
        AzureRoleDefinitionId: {
            Contributor: 'b24988ac-6180-42a0-ab88-20f7382dd24c',
            Owner: '8e3af657-a8ff-443c-a75c-2fe8c4bcb635',
            Reader: 'acdd72a7-3385-48ef-bd42-f606fba81ae7',
            CognitiveServicesUser: 'a97b65f3-24c7-4388-baec-2e87135dc908',
            StorageBlobDataContributor: 'ba92f5b4-2d11-453d-a403-e96b0029c9fe',
            StorageBlobDataOwner: 'b7e6dc6d-f1e8-4753-8033-0f276bb0955b',
            StorageBlobDataReader: '2a2b9908-6ea1-4ae2-8e65-a410df84e7d1'
        },
        QnaAllowedSkuCodes: ['S0'],
        CustomTextAllowedSkuCodes: ['S1'],
        CsFreeSkuCodes: ['F0', 'F1', 'E0'],
        CollaboratorKeyPath: 'userassigned',
        LuisPredictionSkuCodes: ['F0', 'S0'],
        LuisAuthoringSkuCodes: ['F0', 'F1', 'S1', 'E0'],
        LuisAllowedSkuCodes: ['F0', 'F1', 'S0', 'S1', 'E0'],
        AvailableAuthoringAccountsKind: ['LUIS.Authoring', 'QnAMaker'],
        AddRoleAssignmentPermission: 'Microsoft.Authorization/roleAssignments/write',
        TAForHealthRAITermsAcceptedFeatureName: 'LegalTerms.TextAnalytics.TAForHealthRAITermsAccepted',
        TAForPIIRAITermsAcceptedFeatureName: 'LegalTerms.TextAnalytics.TAForPIIRAITermsAccepted',
        CognitiveServicesUserRoleAssignmentPermissions: {
            actions: [
                'Microsoft.Support/*',
                'Microsoft.Insights/metrics/read',
                'Microsoft.CognitiveServices/*/read',
                'Microsoft.Insights/alertRules/read',
                'Microsoft.Resources/subscriptions/read',
                'Microsoft.Insights/logDefinitions/read',
                'Microsoft.Insights/metricdefinitions/read',
                'Microsoft.Insights/diagnosticSettings/read',
                'Microsoft.Resources/deployments/operations/read',
                'Microsoft.ResourceHealth/availabilityStatuses/read',
                'Microsoft.CognitiveServices/accounts/listkeys/action',
                'Microsoft.Resources/subscriptions/resourceGroups/read',
                'Microsoft.Resources/subscriptions/operationresults/read'
            ],
            dataActions: ['Microsoft.CognitiveServices/*'],
            notActions: [] as string[],
            notDataActions: [] as string[]
        },
        StorageBlobDataContributorRoleAssignmentPermissions: {
            actions: [
                'Microsoft.Storage/storageAccounts/blobServices/containers/read',
                'Microsoft.Storage/storageAccounts/blobServices/containers/write',
                'Microsoft.Storage/storageAccounts/blobServices/containers/delete',
                'Microsoft.Storage/storageAccounts/blobServices/generateUserDelegationKey/action'
            ],
            dataActions: [
                'Microsoft.Storage/storageAccounts/blobServices/containers/blobs/read',
                'Microsoft.Storage/storageAccounts/blobServices/containers/blobs/write',
                'Microsoft.Storage/storageAccounts/blobServices/containers/blobs/delete',
                'Microsoft.Storage/storageAccounts/blobServices/containers/blobs/add/action',
                'Microsoft.Storage/storageAccounts/blobServices/containers/blobs/move/action'
            ],
            notActions: [] as string[],
            notDataActions: [] as string[]
        }
    },
    Clu: {
        NoneLabelInConfusionMatrix: '$none',
        MinimumNumberOfLabeledUtterances: 10,
        NumberOfDisplayedUtterancesPerPage: 10,
        GptSupportedRegions: ['eastus', 'southcentralus', 'westeurope'],
        // represents supported authoring regions
        PrivatePreviewAllowedRegions: [
            'eastus',
            'westus2',
            'eastus2',
            'westus3',
            'uksouth',
            'chinaeast2',
            'usgovvirginia',
            'westeurope',
            'northeurope',
            'centralindia',
            'australiaeast',
            'southcentralus',
            'switzerlandnorth',
            'canadacentral'
        ],
        PredictionAllowedRegions: {
            eastus: 'East US',
            eastus2: 'East US 2',
            westus: 'West US',
            westus2: 'West US 2',
            westus3: 'West US 3',
            uksouth: 'UK South',
            ukwest: 'UK West',
            westeurope: 'West Europe',
            chinaeast2: 'China east 2',
            usgovvirginia: 'US Gov Virginia',
            usgovarizona: 'US Gov Arizona',
            northeurope: 'North Europe',
            centralindia: 'Central India',
            australiaeast: 'Australia East',
            southcentralus: 'South Central US',
            switzerlandnorth: 'Switzerland North',
            brazilsouth: 'Brazil South',
            canadacentral: 'Canada Central',
            canadaeast: 'Canada East',
            centralus: 'Central US',
            eastasia: 'East Asia',
            francecentral: 'France Central',
            japaneast: 'Japan East',
            japanwest: 'Japan West',
            jioindiawest: 'Jio India West',
            jioindiacentral: 'Jio India Central',
            koreacentral: 'Korea Central',
            northcentralus: 'North Central US',
            norwayeast: 'Norway East',
            southafricanorth: 'South Africa North',
            southeastasia: 'South East Asia',
            swedencentral: 'Sweden Central',
            westcentralus: 'West Central US',
            uaenorth: 'UAE North',
            qatarcentral: 'Qatar Central'
            // newzealandnorth: 'New Zealand North',
        },
        MaxIntentsCount: 500,
        MinIntentsCount: 1,
        MaxEntitiesCount: 100,
        MinEntitiesCount: 0,
        NoneIntentName: 'None',
        MaxExamplesCount: 15000,
        MinExamplesCount: 1,
        maxNamesCharLength: 50,
        MaxNumOfPrebuiltEntitiesPerCompositeEntity: 7,
        HiddenEntityOptions: ['returnLongestOverlap', 'requireExactOverlap'],
        MaxCountOfSynonymsPerListEntity: 20000,
        RuntimeApiVersion: '2024-11-15-preview',
        AuthoringApiVersion: '2024-11-15-preview',
        InternalApiVersion: '2021-11-01-preview'
    },
    Healthcare: {
        SupportedRegions: ['eastus', 'northeurope', 'uksouth']
    },
    CustomText: {
        SyncingLabelsTimeout: 20000,
        MaximumNumberOfLabelChars: 100,
        MinimumNumberOfTaggedDocuments: 10,
        NoneLabelInConfusionMatrix: '$none',
        MaximumNumberOfTaggedDocuments: 100000,
        MinimumNumberOfEntities: 1,
        MaximumNumberOfEntities: 200,
        MinimumNumberOfClasses: 1,
        MaximumNumberOfClasses: 200,
        maxNamesCharLength: 50,
        MaximumLabelsFilesDisplayed: 5000,
        UnrepresentedEntitiesThreshold: 0,
        OverRepresentedEntitiesThreshold: 50,
        UnderRepresentedEntitiesThreshold: 10,
        InternalApiVersion: '2021-11-01-preview',
        ImportProjectOldVersion: '2021-11-01-preview',
        TrainingApiVersion: '2024-11-15-preview',
        AuthoringApiVersion: '2024-11-15-preview',
        RuntimeApiVersion: '2024-11-15-preview',
        GptSupportedRegions: ['eastus', 'southcentralus', 'westeurope'],
        PrivatePreviewAllowedRegions: [
            'eastus',
            'westus2',
            'eastus2',
            'westus3',
            'uksouth',
            'westeurope',
            'northeurope',
            'centralindia',
            'australiaeast',
            'southcentralus',
            'switzerlandnorth',
            'usgovvirginia',
            'canadacentral'
        ],
        PredictionAllowedRegions: {
            eastus: 'East US',
            eastus2: 'East US 2',
            westus: 'West US',
            westus2: 'West US 2',
            westus3: 'West US 3',
            uksouth: 'UK South',
            ukwest: 'UK West',
            westeurope: 'West Europe',
            northeurope: 'North Europe',
            centralindia: 'Central India',
            australiaeast: 'Australia East',
            southcentralus: 'South Central US',
            switzerlandnorth: 'Switzerland North',
            brazilsouth: 'Brazil South',
            canadacentral: 'Canada Central',
            canadaeast: 'Canada East',
            centralus: 'Central US',
            eastasia: 'East Asia',
            francecentral: 'France Central',
            japaneast: 'Japan East',
            japanwest: 'Japan West',
            jioindiawest: 'Jio India West',
            jioindiacentral: 'Jio India Central',
            koreacentral: 'Korea Central',
            northcentralus: 'North Central US',
            norwayeast: 'Norway East',
            southafricanorth: 'South Africa North',
            southeastasia: 'South East Asia',
            swedencentral: 'Sweden Central',
            westcentralus: 'West Central US',
            uaenorth: 'UAE North',
            qatarcentral: 'Qatar Central',
            eastus2euap: 'East US 2 EUAP',
            usgovvirginia: 'US Gov Virginia'
            // newzealandnorth: 'New Zealand North',
        },
        DefaultLanguageMaxCharacterSizeInBytes: 2,
        LanguageMaxCharacterSizeInBytes: [
            { cultureCode: 'ja', bytes: 4 },
            { cultureCode: 'ko', bytes: 4 },
            { cultureCode: 'en', bytes: 1 },
            { cultureCode: 'en-us', bytes: 1 },
            { cultureCode: 'fr-fr', bytes: 1 },
            { cultureCode: 'it-it', bytes: 1 },
            { cultureCode: 'es-es', bytes: 1 },
            { cultureCode: 'de-de', bytes: 1 },
            { cultureCode: 'pt-br', bytes: 1 },
            { cultureCode: 'pt-pt', bytes: 1 },
            { cultureCode: 'zh-hans', bytes: 4 },
            { cultureCode: 'zh-hant', bytes: 4 },
            // To be revisited
            { languageCode: 'nl', bytes: 2 },
            { languageCode: 'hi', bytes: 2 },
            { languageCode: 'af', bytes: 2 },
            { languageCode: 'am', bytes: 2 },
            { languageCode: 'ar', bytes: 2 },
            { languageCode: 'as', bytes: 2 },
            { languageCode: 'sq', bytes: 2 },
            { languageCode: 'lt', bytes: 2 },
            { languageCode: 'az', bytes: 2 },
            { languageCode: 'bg', bytes: 2 },
            { languageCode: 'eu', bytes: 2 },
            { languageCode: 'fi', bytes: 2 },
            { languageCode: 'et', bytes: 2 },
            { languageCode: 'ga', bytes: 2 },
            { languageCode: 'gl', bytes: 2 },
            { languageCode: 'hu', bytes: 2 },
            { languageCode: 'hr', bytes: 2 },
            { languageCode: 'he', bytes: 2 },
            { languageCode: 'id', bytes: 2 },
            { languageCode: 'hy', bytes: 2 },
            { languageCode: 'kn', bytes: 2 },
            { languageCode: 'lv', bytes: 2 },
            { languageCode: 'ml', bytes: 2 },
            { languageCode: 'ms', bytes: 2 },
            { languageCode: 'pl', bytes: 2 },
            { languageCode: 'pa', bytes: 2 },
            { languageCode: 'ru', bytes: 2 },
            { languageCode: 'ro', bytes: 2 },
            { languageCode: 'gu', bytes: 2 },
            { languageCode: 'sl', bytes: 2 },
            { languageCode: 'sk', bytes: 2 },
            { languageCode: 'sv', bytes: 2 },
            { languageCode: 'uk', bytes: 2 },
            { languageCode: 'te', bytes: 2 },
            { languageCode: 'tr', bytes: 2 },
            { languageCode: 'th', bytes: 2 },
            { languageCode: 'ur', bytes: 2 },
            { languageCode: 'el', bytes: 2 },
            { languageCode: 'da', bytes: 2 },
            { languageCode: 'ta', bytes: 2 },
            { languageCode: 'cs', bytes: 2 },
            { languageCode: 'ca', bytes: 2 },
            { languageCode: 'be', bytes: 2 },
            { languageCode: 'bn', bytes: 2 },
            { languageCode: 'br', bytes: 2 },
            { languageCode: 'bs', bytes: 2 },
            { languageCode: 'eo', bytes: 2 },
            { languageCode: 'tl', bytes: 2 },
            { languageCode: 'ka', bytes: 2 },
            { languageCode: 'ha', bytes: 2 },
            { languageCode: 'jv', bytes: 2 },
            { languageCode: 'kk', bytes: 2 },
            { languageCode: 'km', bytes: 2 },
            { languageCode: 'ku', bytes: 2 },
            { languageCode: 'ky', bytes: 2 },
            { languageCode: 'lo', bytes: 2 },
            { languageCode: 'la', bytes: 2 },
            { languageCode: 'mk', bytes: 2 },
            { languageCode: 'mg', bytes: 2 },
            { languageCode: 'mn', bytes: 2 },
            { languageCode: 'mr', bytes: 2 },
            { languageCode: 'my', bytes: 2 },
            { languageCode: 'ne', bytes: 2 },
            // To be enabled after BE fix
            // { languageCode: 'nb', bytes: 2 },
            { languageCode: 'or', bytes: 2 },
            { languageCode: 'ps', bytes: 2 },
            { languageCode: 'fa', bytes: 2 },
            { languageCode: 'sa', bytes: 2 },
            { languageCode: 'gd', bytes: 2 },
            { languageCode: 'sr', bytes: 2 },
            { languageCode: 'sd', bytes: 2 },
            { languageCode: 'si', bytes: 2 },
            { languageCode: 'so', bytes: 2 },
            { languageCode: 'su', bytes: 2 },
            { languageCode: 'sw', bytes: 2 },
            { languageCode: 'ug', bytes: 2 },
            { languageCode: 'uz', bytes: 2 },
            { languageCode: 'cy', bytes: 2 },
            { languageCode: 'fy', bytes: 2 },
            { languageCode: 'vi', bytes: 2 },
            { languageCode: 'xh', bytes: 2 },
            { languageCode: 'yi', bytes: 2 }
        ]
    },
    CustomHealthcareMonolingualLanguages: ['he'],
    CultureSettings: {
        RTLCultures: ['ar-ar']
    },
    Demos: {
        TaForHealthFHIRVersion: '4.0.1',
        ConversationApiVersion: '2023-11-15-preview',
        SummarizationApiVersion: '2023-11-15-preview',
        ApiVersions: { GA: '2023-04-01', Preview: '2023-11-15-preview' },
        ModelVersions: { GA: 'latest', Preview: '' },
        TaForHealthModelVersions: {
            GA: '2023-12-01',
            Preview: '2022-08-15-preview'
        }
    },
    BackendFeatureFlags: [
        'Microsoft.CognitiveServices/Cloud.TA.CustomDocumentAbstractiveSummarization',
        'Microsoft.CognitiveServices/Cloud.TA.CustomConversationSummarization'
    ],
    HttpResponseCodes: {
        NotFound: '404',
        NotAuthorized: '401',
        RequestThrottled: '429',
        TrainingInProgress: '409',
        NotAuthorizedAction: '403',
        ClientSideFailure: 'Unknown'
    },
    LanguageStudio: {
        AllowedSkuCodes: ['S', 'F0'],
        LegacyHostName: 'language.azure.com',
        HostName: 'language.cognitive.azure.com',
        FairFaxHostName: 'language.cognitive.azure.us',
        MoonCakeHostName: 'language.cognitive.azure.cn',
        LegacyStagingHostName: 'staging.language.azure.com',
        EXHostName: 'language.cognitive.azure.eaglex.ic.gov',
        RXHostName: 'language.cognitive.azure.microsoft.scloud',
        StagingHostName: 'staging.language.cognitive.azure.com',
        StagingEXHostName: 'staging.language.cognitive.azure.eaglex.ic.gov'
    },
    Luis: {
        NoneIntentName: 'None',
        HostName: 'luis.ai',
        StagingHostName: '7uis.ai',
        WestEuropeHostName: 'eu.luis.ai',
        MoonCakeHostName: 'luis.azure.cn',
        FairFaxHostName: 'luis.azure.us',
        EXHostName: 'luis.eaglex.ic.gov',
        CanaryHostName: 'canary.luis.ai',
        NeuralModelTrainingDelayTime: 15000,
        Cultures: {
            NormalizedWordFormsSupportedCultures: ['en-us'],
            PreviewCultures: ['ar-ar', 'hi-in', 'gu-in', 'te-in', 'ta-in', 'mr-in'],
            NeuralModelTrainingSupportedCultures: ['en-us', 'fr-fr', 'es-es', 'de-de', 'it-it', 'pt-br'],
            SpeechPrimingUnsupportedCultures: ['ar-ar', 'zh-cn', 'gu-in', 'hi-in', 'mr-in', 'ta-in', 'te-in'],
            SentimentAnalysisUnsupportedCultures: ['ar-ar', 'zh-cn', 'gu-in', 'hi-in', 'mr-in', 'ta-in', 'te-in'],
            NormalizedDiacriticsSupportedCultures: ['ar-ar', 'pt-br', 'nl-nl', 'fr-fr', 'fr-ca', 'de-de', 'it-it', 'es-es', 'es-mx']
        }
    },
    OperationHeaders: {
        RetryAfterHeader: 'Retry-After',
        LocationHeader: 'operation-location',
        NewLocationHeader: 'Location',
        QnaLocationHeader: 'Location',
        CTLocationHeader: 'operation-location'
    },
    MigrationNotificationToastId: 'migrationNotificationToastId',
    ResourceLimits: {
        ServiceRpsLimit: 5,
        EntityDepthLimit: 4,
        EntityRoleLimit: 10,
        DefaultPageSize: 10,
        EntityPerParentLimit: 10,
        ChunkingServiceLimit: 100,
        UtteranceLengthLimit: 500,
        ServiceFetchSizeLimit: 500,
        AllowedNameReges: '^[a-zA-Z0-9-_.]*$',
        AmlAllowedNameReges: '^[a-zA-Z0-9-_]*$',
        IntentsAndEntitiesForbiddenChars: '\\/:?&*+()%$~%#'
    },
    StorageKeys: {
        ComplianceCookieKey: 'MSCC',
        AzureSessionStorageKey: 'azureSession',
        SessionUrlStorageKey: 'luisSessionUrl',
        TenantSignInStorageKey: 'tenantSignIn',
        PortalSettingsStorageKey: 'portalSettings',
        MigrationModalStorageKey: 'luisMigrationModal',
        LuisDevBaseUrlStorageKey: 'luisDevBaseUrlStorageKey',
        MigratedEntitiesIdsStorageKey: 'migratedEntitiesIdsStorageKey',
        ProductionDevEnvironmentEnabled: 'productionEnvironmentEnabled',
        SummarizationOnboardingModalSeen: 'summarizationOnboardingModalSeen',
        UnifiedAuthoringBaseUrlStorageKey: 'unifiedAuthoringBaseUrlStorageKey',
        LanguageStudioOnboardingModalSeen: 'languageStudioOnboardingModalSeen',
        CluEntityOverlapMethodTutorialSeen: 'cluEntityOverlapMethodTutorialSeen',
        ConfirmSuccessfulMigrationStorageKey: 'confirmSuccessfulMigrationStorageKey',
        LastDismissalMigrationDateStorageKey: 'lastDismissalMigrationDateStorageKey',
        TranslationJobCreationDataStorageKey: 'TranslationJobCreationDataStorageKey',
        NeuralModelTrainingCoachMarkStorageKey: 'NeuralModelTrainingCoachMarkStorageKey',
        NeuralModelTrainingDismissModalStorageKey: 'NeuralModelTrainingDismissModalStorageKey',
        LanguageStudioResourceAcknowledgeStorageKey: 'languageStudioResourceAcknowledgeStorageKey'
    },
    Summarization: {
        SupportedRegions: ['eastus'],
        SupportedLanguages: [
            { code: 'en-us', name: 'English' },
            { code: 'en', name: 'English' }
        ],
        MaximumLabelsFilesDisplayed: 5000,
        maxNamesCharLength: 50,
        MinimumNumberOfTaggedDocuments: 1000,
        MaximumNumberOfTaggedDocuments: 100000,
        RuntimeApiVersion: '2023-04-15-preview',
        MinimumNumberOfTaggedDocumentsForGPT: 10,
        ConversationApiVersion: '2022-10-01-preview',
        CoversationAuthoringVersion: '2023-04-15-preview',
        AbstractiveAuthoringVersion: '2023-04-15-preview',
        SupportedPlatforms: ['cloud', 'docker'] as ('cloud' | 'docker')[]
    },
    TeachingBubbles: {
        Azure: {
            AzureCsAccountCreateModalTenant: 'azureCsAccountCreateModalTenant',
            AzureResourcePicker: 'azureResourcePicker'
        },
        LanguageStudio: {
            LandingPagePivot: 'landingPagePivot'
        },
        Luis: {
            EntityPalette: 'entityPalette',
            NewPredictionsBehavior: 'newPredictionsBehavior',
            Features: 'features',
            IntroTutorial: 'introTutorial',
            LabelReviewEndpointUtterances: 'labelReviewEndpointUtterances',
            LuisSidebarCollapse: 'luisSidebarCollapse'
        },
        Qna: {
            QnaAddQuestionPair: 'qnaAddQuestionPair',
            QnaEdit: 'qnaEdit',
            SynonymEdit: 'synonymEdit',
            QnaTest: 'qnaTest',
            QnaFilter: 'qnaFilter',
            QnaDelete: 'qnaDelete',
            SynonymDelete: 'synonymDelete',
            QnaShowColumns: 'qnaShowColumns',
            QnaSidebarDeploy: 'qnaSidebarDeploy',
            QnaSaveChanges: 'qnaSaveChanges',
            QnaDiscardChanges: 'qnaDiscardChanges',
            SynonymSaveChanges: 'synonymSaveChanges',
            SynonymDiscardChanges: 'synonymDiscardChanges',
            QnaCreateBot: 'qnaCreateBot',
            QnaCreateProject: 'qnaCreateProject',
            QnaAddSource: 'qnaAddSource',
            QnaSidebarEdit: 'qnaSidebarEdit',
            QnaSidebarReviewSuggestion: 'qnaSidebarReviewSuggestion',
            QnaSidebarProjectSettings: 'qnaSidebarProjectSettings',
            QnaActiveReviewSuggestion: 'qnaActiveReviewSuggestion',
            FirstSourceCreated: 'firstSourceCreated',
            QnaAddSynonyms: 'qnaAddSynonyms',
            QnaAzureSearch: 'qnaAzureSearch',
            QnaSelectAzureSearch: 'qnaSelectAzureSearch',
            QnaSideBarCollapseInEditPage: 'QnaSideBarCollapseInEditPage',
            QnaAlternateQuestions: 'QnaAlternateQuestions'
        },
        CustomText: {
            CustomTextAddClass: 'customTextAddClass',
            CustomTextAutoLabel: 'customTextAutoLabel',
            CustomTextAddEntity: 'customTextAddEntity',
            CustomTextExamineModel: 'customTextExamineModel',
            CustomTextCreateProject: 'customTextCreateProject',
            CustomTextSaveTagsButton: 'customTextSaveTagsButton',
            CustomTextTrainModelPage: 'customTextTrainModelPage',
            CustomTextReviewAutotags: 'customTextReviewAutotags',
            CustomTextViewingDropDown: 'customTextViewingDropDown',
            CustomTextTrainingLimitsUI: 'customTextTrainingLimitsUI',
            CustomTextDataTaggingPalette: 'customTextDataTaggingPalette',
            CustomTextAppliedAutotagFilter: 'customTextAppliedAutotagFilter',
            CustomTextAcceptRejectAllAutoTag: 'customTextAcceptRejectAllAutoTag',
            CustomTextMovingBetweenDocuments: 'customTextMovingBetweenDocuments',
            CustomTextFilesViewerSearchButton: 'customTextFilesViewerSearchButton',
            CustomTextAcceptRejectSingleAutoTag: 'customTextAcceptRejectSingleAutoTag'
        },
        CustomSentiment: {
            CustomSentimentAutoLabel: 'customSentimentAutoLabel',
            CustomSentimentExamineModel: 'customSentimentExamineModel',
            CustomSentimentCreateProject: 'customSentimentCreateProject',
            CustomSentimentSaveTagsButton: 'customSentimentSaveTagsButton',
            CustomSentimentTrainModelPage: 'customSentimentTrainModelPage',
            CustomSentimentReviewAutotags: 'customSentimentReviewAutotags',
            CustomSentimentViewingDropDown: 'customSentimentViewingDropDown',
            CustomSentimentTrainingLimitsUI: 'customSentimentTrainingLimitsUI',
            CustomSentimentDataTaggingPalette: 'customSentimentDataTaggingPalette',
            CustomSentimentAppliedAutotagFilter: 'customSentimentAppliedAutotagFilter',
            CustomSentimentMovingBetweenDocuments: 'customSentimentMovingBetweenDocuments',
            CustomSentimentFilesViewerSearchButton: 'customSentimentFilesViewerSearchButton'
        },
        Summarization: {
            SummarizationCreateProject: 'summarizationCreateProject',
            SummarizationFilesViewerSearchButton: 'summarizationFilesViewerSearchButton',
            SummarizationViewingDropDown: 'summarizationViewingDropDown',
            SummarizationAddEntity: 'summarizationAddEntity',
            SummarizationSaveTagsButton: 'summarizationSaveTagsButton',
            SummarizationMovingBetweenDocuments: 'summarizationMovingBetweenDocuments',
            SummarizationDataTaggingPalette: 'summarizationDataTaggingPalette',
            SummarizationAddClass: 'summarizationAddClass',
            SummarizationTrainingLimitsUI: 'summarizationTrainingLimitsUI',
            SummarizationTrainModelPage: 'summarizationTrainModelPage',
            SummarizationExamineModel: 'summarizationExamineModel',
            SummarizationAcceptRejectSingleAutoTag: 'summarizationAcceptRejectSingleAutoTag',
            SummarizationAcceptRejectAllAutoTag: 'summarizationAcceptRejectAllAutoTag',
            SummarizationAppliedAutotagFilter: 'summarizationAppliedAutotagFilter',
            SummarizationReviewAutotags: 'summarizationReviewAutotags'
        },
        Clu: {
            CluCreateProject: 'cluCreateProject',
            CluFilterView: 'cluFilterView',
            CluAddEntity: 'cluAddEntity',
            CluDataTaggingPalette: 'cluDataTaggingPalette',
            CluNewUtterance: 'cluNewUtterance',
            CluTrainModelPage: 'cluTrainingModelPage',
            CluExamineModel: 'cluExamineModel',
            CluModelEvaluation: 'cluModelEvaluation',
            CluMoveUtterance: 'cluMoveUtterance',
            CluEditUtterance: 'cluEditUtterance',
            CluDeleteUtterance: 'cluDeleteUtterance',
            CluSaveChanges: 'cluSaveChanges',
            CluSelectIntent: 'cluSelectIntent',
            CluOverlapButton: 'cluOverlapButton',
            CluLongestOverlapCard: 'cluLongestOverlapCard'
        }
    },
    TeachingBubblesDelay: 1000,
    Telemetry: {
        LuisUserSessionId: 'userSessionId',
        AppInsightsKeyPpe: 'ea2c0bee-2024-4349-b760-1d059c7c133d',
        AppInsightsKeyProduction: '9b916f36-dd88-402e-8bc7-17550f3864db'
    },
    QuestionGeneration: {
        ApiVersion: '2022-10-01-preview',
        NumberOfGeneratedQAPairsPerPage: 30,
        maxDocumentLimit: 25000,
        SupportedProjectLanguages: [{ code: 'en', name: 'English' }]
    },
    QnaMaker: {
        NumberOfDisplayedQnaPairsPerPage: 5,
        ContextTreeMaxDepth: 5,
        SharePointAuthWebAppClientId: 'c2c11949-e9bb-4035-bda8-59542eb907a6',
        ApiVersion: '2021-10-01',
        /*
         * Update QnaMakerLanguageDefaultAnswer file in case a new locale is supported.
         */
        SupportedProjectLangues: [
            { code: 'ar', name: 'Arabic' },
            { code: 'hy', name: 'Armenian' },
            { code: 'bn', name: 'Bengali' },
            { code: 'eu', name: 'Basque' },
            { code: 'bg', name: 'Bulgarian' },
            { code: 'ca', name: 'Catalan' },
            {
                code: 'zh-hans',
                name: 'Chinese_Simplified',
                isChitchatSupported: true
            },
            { code: 'zh-hant', name: 'Chinese_Traditional' },
            { code: 'hr', name: 'Croatian' },
            { code: 'cs', name: 'Czech' },
            { code: 'da', name: 'Danish' },
            { code: 'nl', name: 'Dutch' },
            { code: 'en', name: 'English', isChitchatSupported: true },
            { code: 'et', name: 'Estonian' },
            { code: 'fi', name: 'Finnish' },
            { code: 'fr', name: 'French', isChitchatSupported: true },
            { code: 'gl', name: 'Galician' },
            { code: 'de', name: 'German', isChitchatSupported: true },
            { code: 'el', name: 'Greek' },
            { code: 'gu', name: 'Gujarati' },
            { code: 'he', name: 'Hebrew' },
            { code: 'hi', name: 'Hindi' },
            { code: 'hu', name: 'Hungarian' },
            { code: 'is', name: 'Icelandic' },
            { code: 'id', name: 'Indonesian' },
            { code: 'ga', name: 'Irish' },
            { code: 'it', name: 'Italian', isChitchatSupported: true },
            { code: 'ja', name: 'Japanese', isChitchatSupported: true },
            { code: 'kn', name: 'Kannada' },
            { code: 'ko', name: 'Korean', isChitchatSupported: true },
            { code: 'lv', name: 'Latvian' },
            { code: 'lt', name: 'Lithuanian' },
            { code: 'ml', name: 'Malayalam' },
            { code: 'ms', name: 'Malay' },
            { code: 'nb', name: 'Norwegian' },
            { code: 'pl', name: 'Polish' },
            { code: 'pt', name: 'Portuguese', isChitchatSupported: true },
            { code: 'pa', name: 'Punjabi' },
            { code: 'ro', name: 'Romanian' },
            { code: 'ru', name: 'Russian' },
            { code: 'sr-cryl', name: 'Serbian_Cyrillic' },
            { code: 'sr-latn', name: 'Serbian_Latin' },
            { code: 'sk', name: 'Slovak' },
            { code: 'sl', name: 'Slovenian' },
            { code: 'es', name: 'Spanish', isChitchatSupported: true },
            { code: 'sv', name: 'Swedish' },
            { code: 'ta', name: 'Tamil' },
            { code: 'te', name: 'Telugu' },
            { code: 'th', name: 'Thai' },
            { code: 'tr', name: 'Turkish' },
            { code: 'uk', name: 'Ukrainian' },
            { code: 'ur', name: 'Urdu' },
            { code: 'vi', name: 'Vietnamese' }
        ],
        Metrics: {
            saveClickCounter: 1,
            submitClickCounter: 1,
            editPairBaseClickCounter: 2,
            addNewPairBaseClickCounter: 1,
            addPairBaseTimeInMilliseconds: 0,
            addOrDismissMetadataClickCounter: 2,
            editPairBaseTimeInMilliseconds: 1000,
            addFieldBaseTimeInMilliseconds: 1000,
            addOrDismissAlternateQuestionClickCounter: 2
        },
        whitelistedResources: ['QGenDnd', 'language-dev-frontend-scus'],
        LroPollMaxNumberOfTrials: 180
    },
    TranslationTool: {
        MaxFileSizeMB: 40,
        MaxFilesSizeMB: 250,
        MaxFilesNumber: 1000,
        MaxTargetLanguageSelection: 10,
        AutoGeneratedContainerName: 'language-studio',
        AutoLanguageDetectionItemKey: 'autoLanguageDetectionItemKey',
        whitelistedSubscriptionIds: ['e54a2925-af7f-4b05-9ba1-2155c5fe8a8e', '797cafc6-b871-4a2d-918b-15b0a4ad6193'],
        SupportedFileFormats:
            '.pdf, .csv, .html, .htm, .xlf, .markdown, .mdown, .mkdn, .md, .mkd, .mdwn, .mdtxt, .mdtext, .rmd, .mthml, .mht, .mthml, .mht, .msg, .ppt, .pptx, .doc, .docx, .odt, .odp, .ods, .rtf, .txt'
    },
    operationCall: {
        MAX_TRIALS: 3456000,
        AWAIT_TIME: 3000
    }
};
